import { Link } from 'react-router';
import { ArrowUpRightIcon } from '@heroicons/react/16/solid';
import { Container, SectionHead } from '@/components/layout-primitives';
import { BOOKING_URL, CONTACT_URL, buttonBase } from '@/lib/bw';
import { SERVICES_ENABLED, services } from '@/lib/services';
import { useDocument } from '@/lib/use-document';
import { SERVICES_INDEX_ROUTE } from '@/lib/seo';
import { NotFound } from '@/routes/not-found';

/**
 * Services index. Exists so /services is a real page rather than a 404 sitting
 * above live children, and so every service has an internal link from
 * somewhere other than the nav dropdown.
 *
 * Its metadata lives in lib/seo.ts as SERVICES_INDEX_ROUTE, alongside every
 * other route's.
 */

export function ServicesIndex() {
  useDocument(SERVICES_INDEX_ROUTE);

  /* Rendering the 404 rather than redirecting: the route is switched off, not
     moved, and prerenderRoutes() skips it entirely while it is, so there is no
     /services/index.html for a crawler to find in the first place. */
  if (!SERVICES_ENABLED) return <NotFound />;

  return (
    <main className="bg-white">
      <section className="bg-bw-cream py-16 md:py-24">
        <Container>
          <div className="flex flex-col items-center gap-8 text-center">
            <SectionHead
              as="h1"
              eyebrow="Services"
              title="Everything we do for a Shopify store"
              lead="We are Shopify Partners, and we build on Shopify exclusively. Here is what we do for independent boutiques and artisan food brands. Most stores need several of these at once."
            />
          </div>
        </Container>
      </section>

      <section className="border-t-2 border-bw-ink py-16 md:py-24">
        <Container>
          <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <li key={service.slug}>
                <Link
                  to={`/services/${service.slug}`}
                  className="bw-lift flex h-full flex-col gap-3 rounded-[20px] border-2 border-bw-ink bg-white p-6 shadow-sticker focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-ink focus-visible:ring-offset-2"
                >
                  <h2 className="flex items-start justify-between gap-3 font-display text-[19px] font-bold leading-[1.25] text-bw-ink">
                    {service.name}
                    <ArrowUpRightIcon
                      aria-hidden="true"
                      className="mt-1 h-4 w-4 shrink-0"
                    />
                  </h2>
                  <p className="text-[15px] leading-[1.6] text-ink-700">
                    {service.summary}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Top rule only: the footer draws its own top border, and two 2px rules
          meeting would render as a single 4px line. */}
      <section className="border-t-2 border-bw-ink bg-bw-yellow py-16 md:py-24">
        <Container>
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:justify-between">
            <div className="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
              <p className="bw-eyebrow text-ink-700">Contact us</p>
              <h2 className="bw-display text-[34px] leading-[1.1] text-bw-ink md:text-[44px]">
                Not sure which you need?
              </h2>
              <p className="max-w-[520px] text-[19px] leading-[1.6] text-ink-700">
                Tell us what you sell and where the store is letting you down.
                We&apos;ll tell you which of these actually matter for you.
              </p>
            </div>

            {/* Both destinations are off-site, so plain anchors. */}
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-bw-ink text-white sm:w-auto`}
              >
                Contact us
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
              >
                Book a call
              </a>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}
